import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["formWrapper"];

  connect() {
    const yesSelected = document.getElementById("query_mgmt_yes")?.checked;
    this.toggleVisibility(yesSelected);

    if (!window.__queryManagementLightboxInit) {
      window.__queryManagementLightboxInit = true;
      this.setupGlobalLightbox();
    }

    this.initEditToggle();
    this.initStatusWatcher();
    this.initStatusToggle();
    this.initSelect2Fields(this.element);
    this.refreshCategoryDropdown();

    this.ensureDropzonesVisible();
    this.bindQueryFormSubmit();
  }

  bindQueryFormSubmit() {
    const form = document.getElementById("query-comment-form");
    if (!form || form.dataset.submitBound === "true") return;

    form.dataset.submitBound = "true";
    form.addEventListener("submit", () => {
      if (this._querySubmitting) return;
      this._querySubmitting = true;

      const btn = form.querySelector("#updateQuerySubmitBtn");
      if (btn) {
        btn.disabled = true;
        btn.value = "Updating...";
      }

      if (window.Swal) {
        Swal.fire({
          title: "Updating query...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => Swal.showLoading(),
        });
      }
    });
  }

  showQuerySubmitLoader(isNewQuery) {
    if (!window.Swal) return;
    Swal.fire({
      title: isNewQuery ? "Saving query..." : "Updating query...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    });
  }

  setQuerySubmitBusy(submitEl, busy, isNewQuery) {
    if (!submitEl) return;
    if (!submitEl.dataset.originalLabel) {
      submitEl.dataset.originalLabel = submitEl.textContent.trim();
    }
    submitEl.classList.toggle("opacity-50", busy);
    submitEl.classList.toggle("pointer-events-none", busy);
    submitEl.setAttribute("aria-disabled", busy ? "true" : "false");
    submitEl.textContent = busy
      ? (isNewQuery ? "Saving..." : "Updating...")
      : submitEl.dataset.originalLabel;
  }

  categoryAlreadyAdded(categoryKey) {
    return Boolean(
      document.querySelector(`#query-categories-section [data-category-key="${categoryKey}"]`) ||
      document.querySelector(`#pending-categories-tbody [data-category-key="${categoryKey}"]`)
    );
  }

  refreshCategoryDropdown() {
    const dropdown = document.getElementById("category_dropdown");
    if (!dropdown) return;

    const used = new Set();
    document
      .querySelectorAll("#query-categories-section [data-category-key], #pending-categories-tbody [data-category-key]")
      .forEach((row) => used.add(row.dataset.categoryKey));

    Array.from(dropdown.options).forEach((option, index) => {
      if (index === 0) return;
      const taken = used.has(option.value);
      option.hidden = taken;
      option.disabled = taken;
    });
  }

  addCategoryEntry(event) {
    event.preventDefault();

    const dropdown = document.getElementById("category_dropdown");
    const statusEl = document.getElementById("category_dropdown_status");
    const subjectEl = document.getElementById("category_dropdown_subject");
    const descEl = document.getElementById("category_dropdown_description");
    if (!dropdown || !statusEl || !descEl) return;

    const category = dropdown.value;
    const status = statusEl.value;
    const subject = (subjectEl && subjectEl.value ? subjectEl.value : "").trim();
    const description = descEl.value.trim();
    const label = dropdown.options[dropdown.selectedIndex]
      ? dropdown.options[dropdown.selectedIndex].text.trim()
      : category;

    if (!category) return this.showWarning("Please select a category.");
    if (!subject) return this.showWarning("Please enter a Query Subject for this category.");
    if (!description) return this.showWarning("Please enter a description for this category.");
    if (this.categoryAlreadyAdded(category)) return this.showWarning("This category has already been added.");
    if (this.builderUploadsInProgress()) {
      return this.showWarning("Please wait for screenshot upload to finish before adding the category.");
    }

    const tbody = document.getElementById("pending-categories-tbody");
    if (!tbody) return;

    const existingAttachments = this.collectBuilderAttachments();
    const statusLabel = statusEl.options[statusEl.selectedIndex]
      ? statusEl.options[statusEl.selectedIndex].text.trim()
      : status;
    const uploadUrl = document.getElementById("category-builder")
      ? (document.getElementById("category-builder").dataset.uploadUrl || "")
      : "";
    const prefix = `query_management[category_entries][${category}]`;

    const card = document.createElement("div");
    card.className = "pending-category-card border border-gray-300 rounded-lg overflow-hidden bg-white";
    card.dataset.categoryKey = category;

    card.innerHTML = `
      <div class="px-3 py-2 bg-slate-700 text-white flex items-center justify-between gap-2">
        <div class="text-sm font-semibold">${this.escapeHtml(subject)}</div>
        <button type="button" class="btn btn-link btn-sm p-0"
          data-action="click->query-management#removeCategoryEntry">Remove</button>
      </div>
      <div class="p-3">
        <div class="row g-2 align-items-start">
          <div class="col-md-3">
            <label class="form-label small text-muted mb-1">Query Subject</label>
            <input type="text" class="form-control" name="${prefix}[subject]"
              value="${this.escapeAttr(subject)}" required>
          </div>
          <div class="col-md-3">
            <label class="form-label small text-muted mb-1">Category</label>
            <div class="form-control bg-light">${this.escapeHtml(label)}</div>
          </div>
          <div class="col-md-3">
            <label class="form-label small text-muted mb-1">Status</label>
            <div class="form-control bg-light">${this.escapeHtml(statusLabel)}</div>
          </div>
          <div class="col-md-3">
            <label class="form-label small text-muted mb-1">Screenshot</label>
            <div class="flex flex-wrap gap-2 mb-2" data-existing-previews></div>
            <div class="dropzone dropzone-default dz-clickable qms-category-dropzone"
              data-controller="dropzone"
              data-dropzone-max-file-size="2048"
              data-dropzone-max-files="60"
              data-upload-url="${this.escapeAttr(uploadUrl)}"
              data-dropzone-accepted-files=".jpg,.png,.jpeg,.pdf">
              <input type="file" multiple
                name="qms_pending_category_file"
                data-direct-upload-url="/rails/active_storage/direct_uploads"
                data-dropzone-target="input"
                data-qms-attachment-name="${this.escapeAttr(prefix + "[attachments][]")}"
                class="hidden"
                accept=".jpg,.png,.jpeg,.pdf,image/jpeg,image/png,application/pdf">
              <div class="dropzone-msg dz-message needsclick">
                <span class="dropzone-msg-title">Add screenshot</span>
                <span class="dropzone-msg-desc">JPG / PNG / PDF</span>
              </div>
            </div>
          </div>
          <div class="col-md-12">
            <label class="form-label small text-muted mb-1">Description</label>
            <textarea class="form-control" rows="2" name="${prefix}[description]" required>${this.escapeHtml(description)}</textarea>
          </div>
        </div>
      </div>
      <input type="hidden" name="${prefix}[category]" value="${this.escapeAttr(category)}">
      <input type="hidden" name="${prefix}[status]" value="${this.escapeAttr(status)}">
    `;

    // Persist signed IDs immediately so Save Query works even before dropzone hydrates.
    existingAttachments.forEach(function (attachment) {
      const hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = prefix + "[attachments][]";
      hidden.value = attachment.signedId;
      hidden.dataset.qmsExistingAttachment = "true";
      card.appendChild(hidden);

      if (attachment.dataURL) {
        const previewHost = card.querySelector("[data-existing-previews]");
        if (previewHost) {
          const wrap = document.createElement("div");
          wrap.className = "relative inline-block";
          const img = document.createElement("img");
          img.src = attachment.dataURL;
          img.alt = attachment.name || "Screenshot";
          img.className = "w-20 h-20 object-cover rounded border border-gray-200";
          wrap.appendChild(img);
          previewHost.appendChild(wrap);
        }
      }
    });

    tbody.appendChild(card);

    const wrapper = document.getElementById("pending-categories-wrapper");
    if (wrapper) wrapper.style.display = "";

    // Stimulus MutationObserver connects the new dropzone; do not call
    // application.load(element) — that API expects controller definitions.
    this.resetCategoryBuilder();
    this.refreshCategoryDropdown();
  }

  builderUploadsInProgress() {
    const dropzoneEl = document.getElementById("category-builder-dropzone");
    if (!dropzoneEl) return false;

    const controller = this.application && this.application.getControllerForElementAndIdentifier
      ? this.application.getControllerForElementAndIdentifier(dropzoneEl, "dropzone")
      : null;
    if (!controller || !controller.dropZone || !controller.dropZone.files) return false;

    return controller.dropZone.files.some(function (file) {
      const status = file.status;
      if (status === "uploading" || status === "queued" || status === "added") return true;
      const signedId = (file.controller && file.controller.hiddenInput && file.controller.hiddenInput.value)
        || (file.hiddenInput && file.hiddenInput.value)
        || "";
      return !signedId;
    });
  }

  collectBuilderAttachments() {
    const dropzoneEl = document.getElementById("category-builder-dropzone");
    if (!dropzoneEl) return [];

    const attachments = [];
    const seen = {};
    const controller = this.application && this.application.getControllerForElementAndIdentifier
      ? this.application.getControllerForElementAndIdentifier(dropzoneEl, "dropzone")
      : null;

    if (controller && controller.dropZone && controller.dropZone.files) {
      controller.dropZone.files.forEach(function (file) {
        const signedId = (file.controller && file.controller.hiddenInput && file.controller.hiddenInput.value)
          || (file.hiddenInput && file.hiddenInput.value)
          || null;
        if (!signedId || seen[signedId]) return;
        seen[signedId] = true;

        let dataURL = file.dataURL || null;
        if (!dataURL && file.previewElement) {
          const img = file.previewElement.querySelector("img");
          if (img && img.src) dataURL = img.src;
        }

        attachments.push({
          signedId: signedId,
          name: file.name || "screenshot",
          size: file.size || 1234,
          type: file.type || "image/jpeg",
          dataURL: dataURL
        });
      });
    }

    dropzoneEl.querySelectorAll('input[type="hidden"][name="query_management[category_builder][attachments][]"]').forEach(function (input, index) {
      if (!input.value || seen[input.value]) return;
      seen[input.value] = true;
      attachments.push({
        signedId: input.value,
        name: "screenshot-" + (index + 1),
        size: 1234,
        type: "image/jpeg",
        dataURL: null
      });
    });

    return attachments;
  }

  resetCategoryBuilder() {
    const dropdown = document.getElementById("category_dropdown");
    const statusEl = document.getElementById("category_dropdown_status");
    const subjectEl = document.getElementById("category_dropdown_subject");
    const descEl = document.getElementById("category_dropdown_description");

    if (dropdown) dropdown.value = "";
    if (statusEl) statusEl.value = "open";
    if (subjectEl) subjectEl.value = "";
    if (descEl) descEl.value = "";

    this.clearBuilderDropzone();
  }

  clearBuilderDropzone() {
    const dropzoneEl = document.getElementById("category-builder-dropzone");
    if (!dropzoneEl) return;

    const controller = this.application && this.application.getControllerForElementAndIdentifier
      ? this.application.getControllerForElementAndIdentifier(dropzoneEl, "dropzone")
      : null;

    // Attachments were already copied onto the pending category card. Clearing the
    // builder UI must not call remove_canceled_file (that deletes Azure bytes and
    // leaves broken Screenshot thumbs after Save Query).
    if (controller && controller.dropZone && typeof controller.dropZone.removeAllFiles === "function") {
      (controller.dropZone.files || []).forEach(function (file) {
        file.skipRemoteDelete = true;
      });
      controller.dropZone.removeAllFiles(true);
    }

    dropzoneEl.querySelectorAll('input[type="hidden"][name="query_management[category_builder][attachments][]"]').forEach(function (input) {
      input.remove();
    });
  }

  escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  escapeAttr(value) {
    return this.escapeHtml(value).replace(/'/g, "&#39;");
  }

  removeCategoryEntry(event) {
    event.preventDefault();
    const row = event.target.closest("[data-category-key]");
    if (!row) return;

    row.remove();
    this.refreshCategoryDropdown();

    const tbody = document.getElementById("pending-categories-tbody");
    const wrapper = document.getElementById("pending-categories-wrapper");
    if (tbody && wrapper && tbody.querySelectorAll("[data-category-key]").length === 0) {
      wrapper.style.display = "none";
    }
  }

  updateCategoryDescription(event) {
    const textarea = event.target;
    const description = textarea.value.trim();
    const previousValue = textarea.dataset.originalValue || "";
    const categoryId = textarea.dataset.categoryId;
    const queryId = textarea.dataset.queryId;
    const studyId = textarea.dataset.studyId;

    if (description === previousValue) return;

    if (!description) {
      textarea.value = previousValue;
      return this.showWarning("Description cannot be blank.");
    }

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content;
    const body = new URLSearchParams({
      category_id: categoryId,
      description: description,
      authenticity_token: csrfToken || "",
    });

    fetch(`/studies/${studyId}/query_managements/${queryId}/update_category_description`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken || "",
      },
      body: body.toString(),
    })
      .then(async (resp) => {
        const data = await resp.json();
        if (!resp.ok || !data.success) {
          throw new Error(data.error || "Failed to update description");
        }
        textarea.dataset.originalValue = data.description;
        if (window.toastr) {
          toastr.success("Description updated");
        }
      })
      .catch((err) => {
        textarea.value = previousValue;
        this.showWarning(err.message || "Failed to update description.");
      });
  }

  initSelect2Fields(root = document) {
    if (typeof $ === "undefined" || typeof $.fn.select2 !== "function") return;

    root.querySelectorAll(".select2-search-box").forEach((select) => {
      try {
        if ($(select).hasClass("select2-hidden-accessible")) {
          $(select).select2("destroy");
        }
        $(select).select2({
          width: "100%",
          placeholder: select.dataset.placeholder || "Select options",
        });
      } catch (err) {
        console.warn("Select2 init skipped for query management field:", err);
      }
    });
  }

  ensureDropzonesVisible() {
    const hiddenDropzones = document.querySelectorAll('.dropzone.hidden');
    hiddenDropzones.forEach((dz) => {
      dz.classList.remove('hidden');
    });
  }

  initStatusWatcher() {
    const statusSelect = document.getElementById("query_status");
    const label = document.getElementById("query-comments-label");
    const textarea = document.getElementById("query_comments");

    if (!statusSelect || !label || !textarea) return;

    const resolvedStatuses = [
      "resolved_no_remaining_deficiency", 
      "resolved_deficiency_remains", 
      "closed_not_resolved", 
      "withdrawn"
    ];

    const updateLabel = (value) => {
      if (resolvedStatuses.includes(value)) {
        label.textContent = "Issue Resolution Details / Closure Notes";
        textarea.placeholder = "Enter resolution details or closure notes here...";
      }
      else {
        label.textContent = "Description / Details of the Issue";
        textarea.placeholder = "Enter issue details here...";
      }
    };

    updateLabel(statusSelect.value);

    statusSelect.addEventListener("change", (e) => {
      updateLabel(e.target.value);
    });
  }

  initStatusToggle() {
    const statusSelect = document.getElementById("query_status");
    const editSection = document.getElementById("query-edit-section");
    const editButton = document.getElementById("toggle-edit-section");

    if (statusSelect && editSection) {
      statusSelect.addEventListener("change", () => {
        const selectedStatus = statusSelect.value.toLowerCase();
        
        // Show edit section for statuses that typically require immediate input
        const showEditStatuses = [
          "open",
          "on_hold",
          "resolved_no_remaining_deficiency", 
          "resolved_deficiency_remains",
          "unresolved",
          "withdrawn"
        ];

        if (showEditStatuses.includes(selectedStatus)) {
          editSection.classList.remove("d-none");
          if (editButton) editButton.style.display = "none";
        } else {
          editSection.classList.add("d-none");
          if (editButton) editButton.style.display = "inline-flex";
        }

        this.ensureDropzonesVisible();
      });
    }
  }

  toggleForm(event) {
  const isYes = event.target.value === "yes";

  const queryPanel = document.getElementById("query-panel");
  const holdRadio = document.getElementById("release_eye_hold");
  const holdLabel = document.getElementById("hold-option-label");

  if (isYes) {
    // Show query form and enable Hold option
    queryPanel.classList.remove("d-none");
    if (holdRadio) {
      holdRadio.disabled = false;
      if (holdLabel) holdLabel.style.opacity = "1";
    }
    this.ensureDropzonesVisible();
  } else {
    // Hide query form and disable Hold option
    queryPanel.classList.add("d-none");
    if (holdRadio) {
      holdRadio.disabled = true;
      holdRadio.checked = false; // Uncheck if it was selected
      if (holdLabel) holdLabel.style.opacity = "0.5";
    }
  }
}


  toggleVisibility(show) {
    const panel = document.getElementById("query-panel");
    if (!panel) return;
    if (show) panel.classList.remove("d-none");
    else panel.classList.add("d-none");
  }

  initEditToggle() {
    // Edit toggle is handled via data-action on toggle-edit-section / editQueryBtn.
  }
  toggleResolutionDetails(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const categoryId = button.dataset.categoryId;
    if (!categoryId) return;

    const row = document.querySelector(`[data-resolution-row="${categoryId}"]`);
    if (!row) return;

    const isHidden = row.classList.contains("hidden") || row.style.display === "none";
    if (isHidden) {
      row.classList.remove("hidden");
      row.style.display = "";
      button.setAttribute("aria-expanded", "true");
      const label = button.querySelector("[data-resolution-toggle-label]");
      const icon = button.querySelector("[data-resolution-toggle-icon]");
      if (label) label.textContent = "Hide resolution details";
      if (icon) icon.textContent = "▲";
    } else {
      row.classList.add("hidden");
      button.setAttribute("aria-expanded", "false");
      const label = button.querySelector("[data-resolution-toggle-label]");
      const icon = button.querySelector("[data-resolution-toggle-icon]");
      if (label) label.textContent = "Show resolution details";
      if (icon) icon.textContent = "▼";
    }
  }

  toggleQueryEditSection(event) {
    event.preventDefault();

    const gatedSection = document.querySelector('[data-gate-editing="true"]');
    if (gatedSection) {
      this.setQueryEditMode(true);
      return;
    }

    const editSection = document.getElementById("query-edit-section");
    const editButton = event.currentTarget;
    if (!editSection) return;

    const isHidden =
      editSection.classList.contains("hidden") ||
      editSection.classList.contains("d-none") ||
      editSection.style.display === "none";

    if (isHidden) {
      editSection.classList.remove("hidden", "d-none");
      editSection.style.display = "block";
      if (editButton) editButton.style.display = "none";
      this.ensureDropzonesVisible();
    } else {
      editSection.classList.add("hidden");
      editSection.style.display = "none";
      if (editButton) editButton.style.display = "inline-flex";
    }
  }

  cancelQueryEditSection(event) {
    event.preventDefault();
    this.setQueryEditMode(false);
  }

  setQueryEditMode(active) {
    const gatedSection = document.querySelector('[data-gate-editing="true"]');
    const editActions = document.getElementById("query-edit-actions");
    const editBtn = document.getElementById("editQueryBtn") || document.getElementById("toggle-edit-section");
    const toggleRow = document.getElementById("query-edit-toggle-row");
    const editHint = document.getElementById("query-edit-hint");

    if (!gatedSection) return;

    gatedSection.classList.toggle("query-edit-active", active);
    gatedSection.querySelectorAll(".query-view-field").forEach((el) => {
      el.classList.toggle("hidden", active);
      el.classList.toggle("d-none", active);
    });
    gatedSection.querySelectorAll(".query-edit-field").forEach((el) => {
      el.classList.toggle("hidden", !active);
      el.classList.toggle("d-none", !active);
    });

    gatedSection.querySelectorAll(".category-update-panel, .category-update-row").forEach((panel) => {
      panel.classList.add("hidden", "d-none");
      panel.querySelectorAll("textarea").forEach((textarea) => {
        textarea.value = "";
      });
    });

    // Expand resolution rows while editing so every entry can be updated.
    gatedSection.querySelectorAll("[data-resolution-row]").forEach((row) => {
      if (active) {
        row.classList.remove("hidden");
        row.style.display = "";
      }
    });
    gatedSection.querySelectorAll("[data-action*='toggleResolutionDetails']").forEach((button) => {
      if (active) {
        button.setAttribute("aria-expanded", "true");
        const label = button.querySelector("[data-resolution-toggle-label]");
        const icon = button.querySelector("[data-resolution-toggle-icon]");
        if (label) label.textContent = "Hide resolution details";
        if (icon) icon.textContent = "▲";
      }
    });

    if (!active) {
      gatedSection.querySelectorAll(".category-status-select").forEach((select) => {
        if (select.dataset.originalValue) {
          select.value = select.dataset.originalValue;
        }
      });
      gatedSection.querySelectorAll('textarea[name*="[description]"]').forEach((textarea) => {
        if (typeof textarea.dataset.originalValue !== "undefined") {
          textarea.value = textarea.dataset.originalValue;
        }
      });
      gatedSection.querySelectorAll('textarea[name*="resolution_updates"][name*="[comments]"]').forEach((textarea) => {
        if (typeof textarea.dataset.originalValue !== "undefined") {
          textarea.value = textarea.dataset.originalValue;
        }
      });
      gatedSection.querySelectorAll("[data-existing-attachment]").forEach((el) => {
        el.classList.remove("hidden", "d-none");
      });
      gatedSection.querySelectorAll("[data-remove-attachments-container]").forEach((el) => {
        el.innerHTML = "";
      });
      gatedSection.querySelectorAll("[data-remove-resolution-attachments-container]").forEach((el) => {
        el.innerHTML = "";
      });
    }

    if (editActions) {
      editActions.classList.toggle("hidden", !active);
      editActions.classList.toggle("d-none", !active);
      editActions.classList.toggle("d-flex", active);
      editActions.classList.toggle("flex", active);
      editActions.classList.toggle("justify-content-end", active);
      editActions.classList.toggle("justify-end", active);
      editActions.classList.toggle("gap-2", active);
      editActions.classList.toggle("gap-3", active);
    }
    if (toggleRow) {
      toggleRow.classList.toggle("hidden", active);
      toggleRow.classList.toggle("d-none", active);
    } else if (editBtn) {
      editBtn.style.display = active ? "none" : "";
    }
    if (editHint) {
      editHint.classList.toggle("hidden", !active);
      editHint.classList.toggle("d-none", !active);
    }

    if (active) {
      this.ensureDropzonesVisible();
    }
  }

  handleCategoryStatusChange(event) {
    const select = event.target;
    if (select.dataset.deferSave !== "true") {
      this.updateCategoryStatus(event);
      return;
    }

    const categoryId = select.dataset.categoryId;
    const updatePanel =
      document.getElementById(`category-update-${categoryId}`) ||
      document.querySelector(`tr.category-update-row[data-category-id="${categoryId}"]`);
    if (!updatePanel) return;

    const selectedStatus = select.value;
    const originalStatus = select.dataset.originalValue;

    if (selectedStatus === originalStatus) {
      updatePanel.classList.add("hidden", "d-none");
      return;
    }

    updatePanel.classList.remove("hidden", "d-none");
    this.updateCategoryFollowUpLabels(selectedStatus, updatePanel);
    if (this.ensureDropzonesVisible) {
      this.ensureDropzonesVisible();
    }
  }

  markAttachmentForRemoval(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    const categoryId = button.dataset.categoryId;
    const attachmentKey = button.dataset.attachmentKey;
    if (!categoryId || !attachmentKey) return;

    const thumb = button.closest("[data-existing-attachment]");
    if (thumb) {
      thumb.classList.add("hidden", "d-none");
    }

    const container = document.querySelector(`[data-remove-attachments-container="${categoryId}"]`);
    if (!container) return;

    const existing = container.querySelector(`input[value="${attachmentKey}"]`);
    if (existing) return;

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = `query_management[category_updates][${categoryId}][remove_attachments][]`;
    input.value = attachmentKey;
    container.appendChild(input);
  }

  markResolutionAttachmentForRemoval(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    const threadIndex = button.dataset.threadIndex;
    const attachmentKey = button.dataset.attachmentKey;
    if (threadIndex == null || threadIndex === "" || !attachmentKey) return;

    const thumb = button.closest("[data-existing-attachment]");
    if (thumb) {
      thumb.classList.add("hidden", "d-none");
    }

    const container = document.querySelector(`[data-remove-resolution-attachments-container="${threadIndex}"]`);
    if (!container) return;

    const existing = container.querySelector(`input[value="${attachmentKey}"]`);
    if (existing) return;

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = `query_management[resolution_updates][${threadIndex}][remove_attachments][]`;
    input.value = attachmentKey;
    container.appendChild(input);
  }

  updateCategoryFollowUpLabels(status, row) {
    const resolvedStatuses = [
      "resolved_no_remaining_deficiency",
      "resolved_deficiency_remains",
      "withdrawn",
      "unresolved",
    ];
    const isResolved = resolvedStatuses.includes(status);
    const label = row.querySelector(".category-follow-up-comment-label");
    const textarea = row.querySelector(".category-follow-up-comment");

    if (label) {
      label.textContent = "Resolution details";
    }
    if (textarea) {
      textarea.placeholder = isResolved
        ? "Enter resolution details or closure notes..."
        : "Enter resolution details or update notes...";
    }
  }
  handleStatusChange(event) {
    event.preventDefault();

    const selectedValue = event.target.value;
    const editSection = document.getElementById("query-edit-section");
    const toggleBtn = document.getElementById("editQueryBtn");

    // Description textarea and label
    const commentsLabel = document.getElementById("query-comments-label");
    const commentsTextarea = document.getElementById("query_comments");

    if (!editSection) return;

    // Always show the edit section
    editSection.classList.remove("hidden", "d-none");
    editSection.style.display = "block";

    // Hide Edit Query button if it exists
    if (toggleBtn) {
      toggleBtn.style.display = "none";
    }

    // Ensure Dropzones are visible
    if (this.ensureDropzonesVisible) {
      this.ensureDropzonesVisible();
    }

    // 🔹 Update label and placeholder based on status value
    if (["resolved_no_remaining_deficiency", "resolved_deficiency_remains"].includes(selectedValue)) {
      if (commentsLabel)
        commentsLabel.textContent = "Issue Resolution Details / Closure Notes";
      if (commentsTextarea)
        commentsTextarea.placeholder = "Enter Resolution Details / Closure Notes here...";
    } else {
      if (commentsLabel)
        commentsLabel.textContent = "Description / Details of the Issue";
      if (commentsTextarea)
        commentsTextarea.placeholder = "Type your comment or resolution here...";
    }

    console.log("✅ Query edit section opened for:", selectedValue);
  }

  updateCategoryStatus(event) {
    const select = event.target;
    const categoryId = select.dataset.categoryId;
    const queryId = select.dataset.queryId;
    const studyId = select.dataset.studyId;
    const previousValue = select.dataset.originalValue;
    const status = select.value;

    if (!categoryId || !queryId || !studyId || !status) return;

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content;
    const body = new URLSearchParams({
      category_id: categoryId,
      status: status,
      authenticity_token: csrfToken || "",
    });

    fetch(`/studies/${studyId}/query_managements/${queryId}/update_category_status`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken || "",
      },
      body: body.toString(),
    })
      .then(async (resp) => {
        const data = await resp.json();
        if (!resp.ok || !data.success) {
          throw new Error(data.error || "Failed to update category status");
        }

        select.dataset.originalValue = data.status;

        const aggregateBadge = document.getElementById("query-aggregate-status");
        if (aggregateBadge && data.aggregate_status) {
          aggregateBadge.textContent = data.aggregate_status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        }

        const commitButton = document.getElementById("query-present-submit-button");
        if (commitButton && data.aggregate_status) {
          commitButton.dataset.queryStatus = data.aggregate_status;
        }

        if (window.toastr) {
          toastr.success("Category status updated");
        } else if (window.Swal) {
          Swal.fire({
            icon: "success",
            title: "Category status updated",
            timer: 1500,
            showConfirmButton: false,
          });
        }
      })
      .catch((err) => {
        console.error("Category status update failed:", err);
        select.value = previousValue || select.value;
        if (window.Swal) {
          Swal.fire({
            icon: "error",
            title: "Update Failed",
            text: err.message || "Something went wrong while updating category status.",
          });
        }
      });
  }

  createQuery(event) {
    event.preventDefault();
    if (this._querySubmitting) return;

    const formElement = document.getElementById("query-form");
    if (!formElement) return;

    const submitEl = event.currentTarget;
    const isNewQuery = document.getElementById("category-builder")?.dataset.isNewQuery === "true";
    const pendingCategoryCount = document.querySelectorAll("#pending-categories-tbody [data-category-key]").length;

    const formData = new FormData(formElement);

    if (isNewQuery && pendingCategoryCount === 0) {
      return this.showWarning("Please add at least one category with a Query Subject and description.");
    }

    if (this.builderUploadsInProgress()) {
      return this.showWarning("Please wait for screenshot upload to finish before saving.");
    }

    if (!isNewQuery) {
      const missingResolution = [];
      document.querySelectorAll('[data-gate-editing="true"] .category-status-select').forEach((select) => {
        if (select.value === select.dataset.originalValue) return;
        const categoryId = select.dataset.categoryId;
        const comments = document.getElementById(`category_comments_${categoryId}`);
        if (!comments || !comments.value.trim()) {
          const card = select.closest("[data-category-card]");
          const label = card?.querySelector("[data-category-name]")?.textContent?.trim() || "category";
          missingResolution.push(label);
        }
      });
      if (missingResolution.length > 0) {
        return this.showWarning(
          `Add resolution details before saving status change for: ${missingResolution.join(", ")}.`
        );
      }
    }

    this._querySubmitting = true;
    this.setQuerySubmitBusy(submitEl, true, isNewQuery);
    this.showQuerySubmitLoader(isNewQuery);

    const url = `${document.location.origin}/query_managements`;

    fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content,
        Accept: "application/json",
      },
      body: formData
    })
      .then(async (resp) => {
        const text = await resp.text();
        let data = {};
        try {
          data = text ? JSON.parse(text) : {};
        } catch (_err) {
          throw new Error("Unexpected server response. Please refresh the page.");
        }
        if (!resp.ok) {
          const message = Array.isArray(data.errors) ? data.errors.join(", ") : data.error || "Failed to save query";
          throw new Error(message);
        }
        return data;
      })
      .then((data) => {
        try {
          if (data.panel_html) {
            const panel = document.getElementById("query-panel");
            if (panel) {
              panel.innerHTML = data.panel_html;
              panel.classList.remove("d-none");
              // Controllers reconnect via Stimulus MutationObserver after innerHTML.
              this.rebindThumbnails();
              this.initSelect2Fields(panel);
              this.refreshCategoryDropdown();
              this.setQueryEditMode(false);
              this.ensureDropzonesVisible();
            }
          }
          if (data.commit_button_html) {
            const buttonContainer = document.getElementById("commit-button-container");
            if (buttonContainer) {
              buttonContainer.innerHTML = data.commit_button_html;
            }
          }
          const noRadio = document.getElementById("query_mgmt_no");
          const yesRadio = document.getElementById("query_mgmt_yes");
          if (noRadio && yesRadio) {
            noRadio.disabled = true;
            yesRadio.checked = true;
          }
        } catch (uiErr) {
          console.warn("Query saved but panel refresh failed:", uiErr);
        }

        Swal.fire({
          icon: "success",
          title: "The query is saved and the form is updated",
          confirmButtonText: "OK",
          showConfirmButton: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
        this.triggerQCFormDraftSave();
      })
      .catch((err) => {
        console.error("createQuery error:", err);
        Swal.fire({
          icon: "error",
          title: "Failed to save query",
          text: err.message || "Please try again.",
          confirmButtonText: "OK",
        });
      })
      .finally(() => {
        this._querySubmitting = false;
        this.setQuerySubmitBusy(submitEl, false, isNewQuery);
      });
  }

  triggerQCFormDraftSave() {
    // Dispatch custom event that qc-form controller will listen to
    const event = new CustomEvent('query-saved', { 
      bubbles: true,
      detail: { message: 'Query saved successfully' }
    });
    this.element.dispatchEvent(event);
  }

  showWarning(message) {
    if (window.toastr) {
      toastr.warning(message, "Missing Information");
    } else if (window.Swal) {
      Swal.fire({ icon: "warning", title: "Missing Information", text: message });
    } else {
      alert(message);
    }
  }

  setupGlobalLightbox() {
    document.addEventListener("click", (event) => {
      const el = event.target;
      const thumb = el?.closest?.(".query-attachment-thumb");
      if (thumb) {
        const full = thumb.dataset.fullUrl || thumb.getAttribute("data-full-url");
        const lightboxContainer = document.getElementById("lightbox-container");
        const lightboxImage = document.getElementById("lightbox-image");
        if (lightboxContainer && lightboxImage && full) {
          lightboxImage.src = full;
          lightboxContainer.style.display = "flex";
          lightboxContainer.style.alignItems = "center";
          lightboxContainer.style.justifyContent = "center";
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }

      if (el && (el.id === "lightbox-container" || el.id === "lightbox-image")) {
        this.closeLightbox();
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.closeLightbox();
    });
  }

  closeLightbox() {
    const lightboxContainer = document.getElementById("lightbox-container");
    const lightboxImage = document.getElementById("lightbox-image");
    if (lightboxContainer) lightboxContainer.style.display = "none";
    if (lightboxImage) lightboxImage.src = "";
  }

  rebindThumbnails() {
    document.querySelectorAll(".query-attachment-thumb").forEach(img => {
      if (!img.dataset._bound) {
        img.addEventListener("click", (e) => {
          const full = img.dataset.fullUrl || img.getAttribute("data-full-url");
          const lightboxImage = document.getElementById("lightbox-image");
          const lightboxContainer = document.getElementById("lightbox-container");
          if (lightboxImage && lightboxContainer) {
            lightboxImage.src = full;
            lightboxContainer.style.display = "flex";
            lightboxContainer.style.alignItems = "center";
            lightboxContainer.style.justifyContent = "center";
            e.stopPropagation();
          }
        });
        img.dataset._bound = "1";
      }
    });
  }
  updateStatus(event) {
    const selectedStatus = event.target.value;
    const queryId = event.target.dataset.id;

    const finalStatuses = ["resolved_no_remaining_deficiency", "resolved_deficiency_remains", "unresolved", "withdrawn", "on_hold"];
    
    if (finalStatuses.includes(selectedStatus)) {
      const statusLabels = {
        "resolved_no_remaining_deficiency": "Resolved - No Action",
        "resolved_deficiency_remains": "Resolved - Action Taken", 
        "unresolved": "Closed -  UnResolved",
        "withdrawn": "Withdrawn",
        "on_hold": "On Hold"
      };
      
      Swal.fire({
        title: "Are you sure?",
        text: `Do you really want to mark this query as "${statusLabels[selectedStatus]}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, update it",
        cancelButtonText: "Cancel"
      }).then((result) => {
        if (result.isConfirmed) {
          this.sendStatusUpdate(queryId, selectedStatus);
        } else {
          // Revert dropdown selection if cancelled
          event.target.value = event.target.dataset.originalValue || "open";
        }
      });
    } else {
      this.sendStatusUpdate(queryId, selectedStatus);
    }
  }

  sendStatusUpdate(id, status) {
  fetch(`/query_managements/${id}/update_status`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']").content,
    },
    body: JSON.stringify({ status }),
  })
    .then(async (resp) => {
      const text = await resp.text();
      if (!text) throw new Error("Empty response from server");
      return JSON.parse(text);
    })
    .then((data) => {
      if (data.success) {
        // ✅ Update the dropdown visually with appropriate colors
        const dropdown = document.getElementById("status-dropdown");
        if (dropdown) {
          const statusColors = {
            "open": "bg-blue-500",
            "on_hold": "bg-orange-500",
            "resolved_no_remaining_deficiency": "bg-green-600",
            "resolved_deficiency_remains": "bg-green-700",
            "unresolved": "bg-gray-600",
            "withdrawn": "bg-gray-500"
          };
          
          const statusLabels = {
            "open": "Open",
            "on_hold": "On Hold",
            "responded_under_review": "Responded – Under Review",
            "resolved_no_remaining_deficiency": "Resolved – No Remaining Deficiency",
            "resolved_deficiency_remains": "Resolved – Deficiency Remains",
            "unresolved": "Unresolved",
            "withdrawn": "Withdrawn"
          };
          
          const colorClass = statusColors[data.new_status] || "bg-blue-500";
          const label = statusLabels[data.new_status] || data.new_status;
          
          dropdown.outerHTML = `
            <span class="px-3 py-1 rounded text-white ${colorClass}">
              ${label}
            </span>`;
        }

        // ✅ SweetAlert success confirmation
        Swal.fire({
          icon: "success",
          title: "Query Updated!",
          text: `The query status has been successfully updated.`,
          confirmButtonText: "OK",
          confirmButtonColor: "#3085d6",
        });

      } else {
        // ❌ Error handling via Swal
        Swal.fire({
          icon: "error",
          title: "Update Failed",
          text: data.error || "Something went wrong while updating the query status.",
          confirmButtonText: "OK",
          confirmButtonColor: "#d33",
        });
      }
    })
    .catch((err) => {
      console.error("Status update failed:", err);
      Swal.fire({
        icon: "error",
        title: "Network Error",
        text: "Something went wrong while updating status. Please try again.",
        confirmButtonText: "OK",
        confirmButtonColor: "#d33",
      });
    });
  }
  collectPrintCategories() {
    const rows = [];
    const categoriesSection = document.getElementById("query-categories-section");
    if (!categoriesSection) return rows;

    const cardRows = categoriesSection.querySelectorAll("[data-category-card]");
    if (cardRows.length > 0) {
      cardRows.forEach((card) => {
        const category = card.querySelector("[data-category-name]")?.innerText?.trim();
        if (!category) return;

        const statusSelect = card.querySelector(".category-status-select");
        const statusBadge = card.querySelector(".query-view-field");
        const status = statusSelect && !statusSelect.classList.contains("hidden")
          ? statusSelect.options[statusSelect.selectedIndex]?.text?.trim()
          : statusBadge?.innerText?.trim();

        const description = card.querySelector("[data-category-issue]")?.innerText?.trim();
        const subject = card.querySelector("[data-category-subject]")?.innerText?.trim();

        rows.push({
          category,
          status: status || "N/A",
          subject: subject || "—",
          description: description || "—",
        });
      });
      return rows;
    }

    categoriesSection.querySelectorAll("tbody tr").forEach((row) => {
      const cells = row.querySelectorAll("td");
      const category = cells[0]?.innerText?.trim();
      if (!category) return;

      const statusSelect = cells[1]?.querySelector("select");
      const statusBadge = cells[1]?.querySelector(".badge");
      const status = statusSelect
        ? statusSelect.options[statusSelect.selectedIndex]?.text?.trim()
        : statusBadge?.innerText?.trim();

      const subject = cells[2]?.innerText?.trim();
      const descriptionInput = cells[3]?.querySelector("textarea");
      const description = descriptionInput
        ? descriptionInput.value.trim()
        : cells[3]?.innerText?.trim();

      rows.push({
        category,
        status: status || "N/A",
        subject: subject || "—",
        description: description || "—",
      });
    });

    return rows;
  }

  collectPrintResolutions() {
    const entries = [];
    document.querySelectorAll("[data-category-card]").forEach((card) => {
      const category = card.querySelector("[data-category-name]")
        ? card.querySelector("[data-category-name]").innerText.trim()
        : "—";
      const categoryId = card.dataset.categoryId;
      const resolutionRow = categoryId
        ? document.querySelector(`[data-resolution-row="${categoryId}"]`)
        : null;
      const resolutionEntries = resolutionRow
        ? resolutionRow.querySelectorAll(".category-resolution-entry")
        : [];

      if (resolutionEntries.length > 0) {
        resolutionEntries.forEach((entry) => {
          const entryAttachments = entry.querySelector("[data-print-attachments]");
          entries.push({
            category: category,
            attachmentsHtml: entryAttachments ? entryAttachments.innerHTML : "",
            body: entry.querySelector("[data-print-body]")
              ? entry.querySelector("[data-print-body]").innerText.trim()
              : "",
            meta: entry.querySelector("[data-print-meta]")
              ? entry.querySelector("[data-print-meta]").innerText.trim()
              : ""
          });
        });
      }
    });
    return entries;
  }

  buildPrintTable(headers, rows, className = "custom-table") {
    const table = document.createElement("table");
    table.classList.add(className);

    if (headers?.length) {
      const headerRow = table.insertRow();
      headers.forEach((text) => {
        const cell = headerRow.insertCell();
        cell.innerText = text;
        cell.style.fontWeight = "bold";
        cell.style.padding = "8px 10px";
        cell.style.border = "1px solid #ddd";
        cell.style.backgroundColor = "#f0f0f0";
      });
    }

    rows.forEach((rowValues) => {
      const row = table.insertRow();
      rowValues.forEach((value) => {
        const cell = row.insertCell();
        cell.innerText = value;
        cell.style.padding = "8px 10px";
        cell.style.border = "1px solid #ddd";
        cell.style.verticalAlign = "top";
      });
    });

    return table;
  }

  printQuery() {
    const studyName =
      document.getElementById("studyShortName")?.value ||
      document.getElementById("studyID")?.value ||
      "Study";
    const modalityName = document.getElementById("modalityID")?.value || "";
    const queryTitle =
      document.querySelector("input[name='query_management[title]']")?.value || "Query";
    const overallStatus =
      document.getElementById("query-aggregate-status")?.innerText?.trim() || "N/A";
    const categoryRows = this.collectPrintCategories();

    const summaryTable = this.buildPrintTable(
      ["Field", "Value"],
      [
        ["Query Title", queryTitle],
        ["Overall Status", overallStatus],
      ]
    );

    const categoriesTable = this.buildPrintTable(
      ["Query Subject", "Category", "Status", "Description"],
      categoryRows.length > 0
        ? categoryRows.map((row) => [row.subject || "—", row.category, row.status, row.description])
        : [["—", "—", "—", "—"]]
    );

    const categoriesHeading = document.createElement("h3");
    categoriesHeading.innerText = "Categories";
    categoriesHeading.style.fontSize = "16px";
    categoriesHeading.style.margin = "18px 0 8px";

    const resolutionEntries = this.collectPrintResolutions();
    let commentsTable = null;
    let historyHeading = null;

    if (resolutionEntries.length > 0) {
      historyHeading = document.createElement("h3");
      historyHeading.innerText = "Resolution Details";
      historyHeading.style.fontSize = "16px";
      historyHeading.style.margin = "18px 0 8px";

      commentsTable = this.buildPrintTable(
        ["Category", "Attachments", "Details"],
        resolutionEntries.map((entry) => [
          entry.category,
          entry.attachmentsHtml ? "" : "None",
          `${entry.body}\n${entry.meta}`.trim(),
        ])
      );

      commentsTable.querySelectorAll("tbody tr").forEach((row, index) => {
        const entry = resolutionEntries[index];
        if (entry?.attachmentsHtml) {
          row.cells[1].innerHTML = entry.attachmentsHtml;
        }
        row.cells[2].style.whiteSpace = "pre-wrap";
      });
    } else {
      const commentRows = document.querySelectorAll("#comments-table tbody tr");
      if (commentRows.length === 0) {
        commentsTable = null;
        historyHeading = null;
      } else {
      historyHeading = document.createElement("h3");
      historyHeading.innerText = "Query History";
      historyHeading.style.fontSize = "16px";
      historyHeading.style.margin = "18px 0 8px";

      commentsTable = document.createElement("table");
      commentsTable.classList.add("form-table");

      commentRows.forEach((row) => {
        if (row.querySelector("td[colspan]")) return;

        const printRow = commentsTable.insertRow();
        const originalCells = row.querySelectorAll("td");
        const originalAttachmentCell = originalCells[0];
        const originalCategoryCell = originalCells.length >= 3 ? originalCells[1] : null;
        const originalDetailCell = originalCells.length >= 3 ? originalCells[2] : originalCells[1];
        if (!originalAttachmentCell || !originalDetailCell) return;

        const attachmentCell = printRow.insertCell();
        attachmentCell.style.width = originalCategoryCell ? "25%" : "30%";
        attachmentCell.style.verticalAlign = "top";
        attachmentCell.style.border = "1px solid #ddd";
        attachmentCell.style.padding = "10px";

        originalAttachmentCell.querySelectorAll("img").forEach((img) => {
          attachmentCell.appendChild(img.cloneNode(true));
        });

        const seenFiles = new Set();
        originalAttachmentCell.querySelectorAll("a[href]").forEach((link) => {
          if (link.querySelector("img")) return;

          const fileName = link.textContent.trim();
          if (!fileName || seenFiles.has(fileName)) return;

          seenFiles.add(fileName);

          const fileDiv = document.createElement("div");
          fileDiv.style.marginBottom = "6px";

          const fileLink = document.createElement("a");
          fileLink.href = link.href;
          fileLink.textContent = fileName;
          fileLink.target = "_blank";
          fileLink.style.fontSize = "13px";
          fileLink.style.color = "#17365d";
          fileLink.style.textDecoration = "none";

          fileDiv.appendChild(fileLink);
          attachmentCell.appendChild(fileDiv);
        });

        if (attachmentCell.children.length === 0) {
          attachmentCell.innerHTML = "<em>No Attachments</em>";
        }

        if (originalCategoryCell) {
          const categoryCell = printRow.insertCell();
          categoryCell.style.width = "20%";
          categoryCell.style.verticalAlign = "top";
          categoryCell.style.border = "1px solid #ddd";
          categoryCell.style.padding = "10px";
          categoryCell.innerText = originalCategoryCell.innerText.trim() || "—";
        }

        const detailCell = printRow.insertCell();
        detailCell.style.width = originalCategoryCell ? "55%" : "70%";
        detailCell.style.verticalAlign = "top";
        detailCell.style.border = "1px solid #ddd";
        detailCell.style.padding = "10px";

        const detailClone = originalDetailCell.cloneNode(true);
        detailClone.querySelectorAll("select, button, input, textarea").forEach((el) => el.remove());
        detailCell.innerHTML = detailClone.innerHTML;
      });
      }
    }

    // ===== OPEN PRINT WINDOW =====
    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <title>${modalityName} - Query history for QC form</title>
        <style>
          body {
            font-family: Calibri, sans-serif;
            margin: 20px;
            color: #17365d;
          }

          .header-container {
            display: grid;
            grid-template-columns: 1fr 2fr 1fr;
            align-items: center;
            gap: 10px;
            margin-bottom: 25px;
          }

          .left-text {
            font-size: 18px;
            font-weight: bold;
            line-height: 1.3;
          }

          .study-title {
            text-align: center;
            word-wrap: break-word;
          }

          .study-title .study-name {
            font-size: 22px;
            font-weight: bold;
          }

          .study-title .modality-line {
            font-size: 20px;
            margin-top: 12px;
            color: #444;
          }

          .logo {
            width: 180px;
            justify-self: end;
          }

          .custom-table,
          .form-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 16px;
          }

          h3 {
            color: #17365d;
            font-weight: bold;
          }

          img {
            max-width: 140px;
            height: auto;
            margin: 5px;
          }

          tr {
            page-break-inside: avoid;
          }

          @media print {
            body {
              margin: 10mm;
            }
          }
        </style>
      </head>

      <body>
        <div class="header-container">
          <div class="left-text">
            OCULAR IMAGING <br />
            RESEARCH & <br />
            READING CENTER
          </div>

          <div class="study-title">
            <div class="study-name">${studyName}</div>
            ${
              modalityName
                ? `<div class="modality-line">${modalityName} - Query history for QC form</div>`
                : ""
            }
          </div>

          <img
            src="${document
              .querySelector('meta[name="asset-base-path"]')
              .getAttribute('content')}"
            class="logo"
          />
        </div>
      </body>
      </html>
    `);

    printWindow.document.body.appendChild(summaryTable);
    printWindow.document.body.appendChild(categoriesHeading);
    printWindow.document.body.appendChild(categoriesTable);
    if (historyHeading && commentsTable) {
      printWindow.document.body.appendChild(historyHeading);
      printWindow.document.body.appendChild(commentsTable);
    }

    printWindow.document.close();
    printWindow.focus();
  }
  showStatusInfo() {
    Swal.fire({
      title: "Status Descriptions",
      html: `
        <div class="text-left text-sm space-y-4 max-h-[70vh] overflow-y-auto pr-2">

          <p>
            <strong>Open</strong>: A discrepancy in the imaging package received with respect to the imaging standardization procedures
            outlined in the imaging manual/protocol and the QC charter flagged by the QCO that will require and/or is
            awaiting resolution. For example, including but not limited to improper anonymization, uncertified
            operators/devices, incomplete/incorrect images.
          </p>

          <p>
            <strong>On Hold</strong>: The QC form has been completed and submitted, but the case is intentionally not released to
            graders while the query remains pending further action or information. The package is held until the query
            can progress to another status (e.g. resolved, withdrawn, or closed).
          </p>

          <p>
            <strong>Resolved – No Remaining Deficiency</strong>: A discrepancy in the imaging package received with respect to the imaging
            standardization procedures outlined in the imaging manual/protocol and the QC charter flagged by the QCO
            that has been confirmed by the responsible external/internal party to have undergone intervention or further
            activity that resulted in the desirable resolution bringing the query to comply with standardized documents
            including imaging manual, QC charter and study protocol. Examples include but are not limited to:
          </p>

          <ol class="list-decimal list-inside ml-4 space-y-1">
            <li>Missing data that has been retrieved and uploaded to OIRRC.</li>
            <li>
              Date of capture, site ID, site name, subject ID, subject name, device ID, certified photographer etc
              corrected in the system or corrected and re-uploaded as required.
            </li>
          </ol>

          <p>
            <strong>Resolved – Deficiency Remains</strong>: A discrepancy in the imaging package received with respect to the imaging
            standardization procedures outlined in the imaging manual/protocol flagged by the QC charter and the QCO
            that has been confirmed externally to require no intervention or no further activity. The underlying issue
            may still persist. Examples include:
          </p>

          <ol class="list-decimal list-inside ml-4 space-y-1">
            <li>Subject having trouble keeping eyes open during imaging thus only half of the set of images were acquired.</li>
            <li>A particular lens broke thus another lens was used to acquire images that may not comply with imaging manual.</li>
            <li>The subject was sick and did not show up to the visit, thus no data existed.</li>
          </ol>

          <p>
            <strong>Unresolved</strong>: A discrepancy in the imaging package received with respect to the imaging standardization procedures
            outlined in the imaging manual/protocol and the QC charter flagged by the QCO that has not been responded
            to in 15 business days and confirmed by Project Management to not pursue further. The underlying concern
            remains present.
          </p>

          <p>
            <strong>Withdrawn</strong>: A false discrepancy in the imaging package with respect to the imaging standardization procedures
            outlined in the imaging standardization protocol and the QC Charter flagged by the QCO and/or PM. The
            query was subsequently recalled, and external stakeholders notified that the package is compliant with the
            standardization protocols outlined in respective documents. Examples include but are not limited to:
          </p>

          <ol class="list-decimal list-inside ml-4 space-y-1">
            <li>
              Technical faults within the Reading Center not allowing image processing
              (error in EZDA tool, submission error, outdated software).
            </li>
            <li>
              Erroneous identification of subject ID, visit date, or visit label.
            </li>
          </ol>

        </div>
      `,
      width: "80vw",
      confirmButtonText: "Got it",
      confirmButtonColor: "#4f46e5",
    })
  }

}
